import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  requestNotificationPermission,
  onMessageListener,
  getNotificationPermission,
  isFCMSupported,
  IS_DEMO_MODE
} from '../services/firebase';
import axios from 'axios';
import '../styles/common.css';

function ChildSettings() {
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState({
    notifications: true,
    sound: true,
    dailyReminder: true,
    reminderTime: '18:00'
  });
  const [loading, setLoading] = useState(true);
  const [notificationStatus, setNotificationStatus] = useState({
    permission: 'default',
    supported: false,
    token: null
  });
  const [testLoading, setTestLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // 로그인 상태 확인
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/child/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.type !== 'child') {
        navigate('/');
        return;
      }
      setUser(parsedUser);
      // loadSettings(); // TODO: 실제 설정 로드
      checkNotificationStatus();
      setLoading(false);
    } catch (error) {
      console.error('사용자 데이터 파싱 오류:', error);
      navigate('/child/login');
    }
  }, [navigate]);

  // 알림 상태 확인
  const checkNotificationStatus = () => {
    setNotificationStatus({
      permission: getNotificationPermission(),
      supported: isFCMSupported(),
      token: null
    });
  };

  // 알림 권한 요청 및 토큰 수집
  const handleEnableNotifications = async () => {
    try {
      setTestLoading(true);

      if (IS_DEMO_MODE) {
        // 데모 모드에서는 실제 권한 요청 없이 시뮬레이션
        setNotificationStatus({
          permission: 'granted',
          supported: true,
          token: 'demo-token'
        });

        // 서버에 데모 토큰 저장
        await axios.post('/api/v0/auth/notify/token', {
          token: 'demo-fcm-token-for-child-' + user.id,
          platform: 'web'
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        alert('🎉 알림이 활성화되었습니다!\n(데모 모드)');
      } else {
        // 실제 모드에서는 FCM 토큰 요청
        const token = await requestNotificationPermission();

        setNotificationStatus({
          permission: 'granted',
          supported: true,
          token: token
        });

        // 서버에 토큰 저장
        await axios.post('/api/v0/auth/notify/token', {
          token: token,
          platform: 'web'
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        alert('🎉 알림이 활성화되었습니다!');

        // 포그라운드 메시지 리스너 설정
        onMessageListener().then((payload) => {
          console.log('메시지 수신:', payload);
        }).catch((error) => {
          console.log('메시지 리스너 오류:', error);
        });
      }

    } catch (error) {
      console.error('알림 활성화 실패:', error);
      alert('알림 활성화에 실패했습니다.\n' + error.message);
    } finally {
      setTestLoading(false);
    }
  };

  // 테스트 알림 전송
  const handleTestNotification = async () => {
    try {
      setTestLoading(true);

      const response = await axios.post('/api/v0/auth/notify/test', {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.demo) {
        // 데모 모드에서는 브라우저 알림으로 시뮬레이션
        if (Notification.permission === 'granted' || IS_DEMO_MODE) {
          new Notification('🔔 테스트 알림', {
            body: '도토리 저금통 알림이 정상적으로 작동합니다!',
            icon: '/favicon.ico',
            tag: 'test-notification'
          });
        }
        alert('📱 테스트 알림이 전송되었습니다!\n(데모 모드)');
      } else {
        alert('📱 테스트 알림이 전송되었습니다!');
      }

    } catch (error) {
      console.error('테스트 알림 전송 실패:', error);
      alert('테스트 알림 전송에 실패했습니다.');
    } finally {
      setTestLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    // TODO: 설정 저장 API 호출
  };

  const handleDeleteAccount = () => {
    if (window.confirm('정말로 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      // TODO: 계정 삭제 API 호출
      alert('부모님께 계정 삭제를 요청했습니다.');
    }
  };

  if (!user) {
    return (
      <div className="page-container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            로딩 중...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* 헤더 */}
      <div className="page-header">
        <Link to="/child/dashboard" className="back-btn">← 뒤로가기</Link>
        <h1>⚙️ 설정</h1>
        <button onClick={handleLogout} className="btn btn-outline btn-sm">
          로그아웃
        </button>
      </div>

      <div className="page-content">
        {/* 프로필 카드 */}
        <div className="profile-card">
          <div className="profile-content">
            <div className="profile-avatar">👤</div>
            <div className="profile-info">
              <h3>{user.name}</h3>
              <p>가족: {user.familyName}</p>
            </div>
          </div>
        </div>

        {/* 알림 설정 */}
        <div className="settings-card">
          <h3>🔔 알림 설정</h3>
          <div className="settings-list">
            <div className="setting-item">
              <div className="setting-info">
                <h4>알림 받기</h4>
                <p>미션 완료, 승인 등의 알림을 받습니다</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.notifications}
                  onChange={(e) => handleSettingChange('notifications', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h4>알림 소리</h4>
                <p>알림이 올 때 소리를 재생합니다</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.sound}
                  onChange={(e) => handleSettingChange('sound', e.target.checked)}
                  disabled={!settings.notifications}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h4>일일 리마인더</h4>
                <p>매일 정해진 시간에 미션 알림을 받습니다</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.dailyReminder}
                  onChange={(e) => handleSettingChange('dailyReminder', e.target.checked)}
                  disabled={!settings.notifications}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {settings.dailyReminder && settings.notifications && (
              <div className="setting-item">
                <div className="setting-info">
                  <h4>리마인더 시간</h4>
                  <p>일일 리마인더를 받을 시간을 설정합니다</p>
                </div>
                <input
                  type="time"
                  value={settings.reminderTime}
                  onChange={(e) => handleSettingChange('reminderTime', e.target.value)}
                  className="time-input"
                />
              </div>
            )}
          </div>
        </div>

        {/* 푸시 알림 설정 */}
        <div className="settings-card">
          <h3>🔔 푸시 알림</h3>
          <div className="notification-status">
            <div className="status-info">
              <p><strong>알림 지원:</strong> {notificationStatus.supported ? '✅ 지원됨' : '❌ 미지원'}</p>
              <p><strong>권한 상태:</strong>
                {notificationStatus.permission === 'granted' ? ' ✅ 허용됨' :
                 notificationStatus.permission === 'denied' ? ' ❌ 거부됨' : ' ⏳ 대기중'}
              </p>
              {IS_DEMO_MODE && <p className="demo-badge">🧪 데모 모드</p>}
            </div>

            <div className="notification-actions">
              {notificationStatus.permission !== 'granted' && (
                <button
                  onClick={handleEnableNotifications}
                  disabled={testLoading || !notificationStatus.supported}
                  className="btn btn-primary btn-full"
                >
                  {testLoading ? '설정 중...' : '🔔 알림 활성화'}
                </button>
              )}

              {(notificationStatus.permission === 'granted' || IS_DEMO_MODE) && (
                <button
                  onClick={handleTestNotification}
                  disabled={testLoading}
                  className="btn btn-outline btn-full"
                >
                  {testLoading ? '전송 중...' : '📱 테스트 알림'}
                </button>
              )}
            </div>

            <div className="notification-help">
              <h4>📢 알림을 받는 경우</h4>
              <ul>
                <li>• 부모님이 미션을 승인/반려했을 때</li>
                <li>• 새로운 미션이 추가되었을 때</li>
                <li>• 용돈 정산이 완료되었을 때</li>
                <li>• 매일 미션 리마인더 (설정한 시간)</li>
              </ul>
              <p className="help-note">
                💡 알림을 받으려면 위의 '알림 활성화' 버튼을 눌러주세요!
                {!notificationStatus.supported && ' (현재 환경에서는 알림이 지원되지 않습니다)'}
              </p>
            </div>
          </div>
        </div>

        {/* 앱 정보 */}
        <div className="settings-card">
          <h3>ℹ️ 앱 정보</h3>
          <div className="info-list">
            <div className="info-item">
              <span className="info-label">앱 버전</span>
              <span className="info-value">1.0.0</span>
            </div>
            <div className="info-item">
              <span className="info-label">마지막 업데이트</span>
              <span className="info-value">2025년 9월</span>
            </div>
          </div>
        </div>

        {/* 도움말 및 지원 */}
        <div className="settings-card">
          <h3>❓ 도움말 및 지원</h3>
          <div className="help-list">
            <div className="help-item" onClick={() => alert('도움말 페이지를 준비 중입니다.')}>
              <div className="help-icon">📚</div>
              <div className="help-info">
                <h4>사용법 보기</h4>
                <p>앱 사용법을 자세히 알아보세요</p>
              </div>
              <div className="help-arrow">›</div>
            </div>
            <div className="help-item" onClick={() => alert('부모님께 문의해주세요.')}>
              <div className="help-icon">💬</div>
              <div className="help-info">
                <h4>문의하기</h4>
                <p>궁금한 점이 있으면 언제든지 문의하세요</p>
              </div>
              <div className="help-arrow">›</div>
            </div>
          </div>
        </div>

        {/* 계정 관리 */}
        <div className="settings-card danger-card">
          <h3>⚠️ 계정 관리</h3>
          <div className="danger-content">
            <p>계정을 삭제하면 모든 데이터가 영구적으로 삭제됩니다.</p>
            <button
              onClick={handleDeleteAccount}
              className="btn btn-danger btn-full"
            >
              계정 삭제 요청
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .page-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding-bottom: 20px;
        }

        .page-header {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          padding: 15px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }


        .page-header h1 {
          margin: 0;
          color: #764ba2;
          font-size: 1.3rem;
          font-weight: bold;
        }

        .page-content {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
        }

        .profile-card, .settings-card {
          background: white;
          border-radius: 15px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.1);
        }

        .profile-content {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .profile-avatar {
          font-size: 3rem;
          width: 80px;
          height: 80px;
          background: #f8f9fa;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .profile-info h3 {
          margin: 0 0 5px 0;
          color: #333;
          font-size: 1.3rem;
        }

        .profile-info p {
          margin: 0;
          color: #666;
          font-size: 1rem;
        }

        .settings-card h3 {
          color: #333;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .settings-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .setting-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 20px;
          border-bottom: 1px solid #f0f0f0;
        }

        .setting-item:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .setting-info h4 {
          margin: 0 0 5px 0;
          color: #333;
          font-size: 1rem;
        }

        .setting-info p {
          margin: 0;
          color: #666;
          font-size: 0.9rem;
        }

        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 50px;
          height: 28px;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: .4s;
          border-radius: 28px;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 20px;
          width: 20px;
          left: 4px;
          bottom: 4px;
          background-color: white;
          transition: .4s;
          border-radius: 50%;
        }

        input:checked + .toggle-slider {
          background-color: #764ba2;
        }

        input:checked + .toggle-slider:before {
          transform: translateX(22px);
        }

        input:disabled + .toggle-slider {
          background-color: #e9ecef;
          cursor: not-allowed;
        }

        .time-input {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 1rem;
          background: #f8f9fa;
        }

        .info-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #f0f0f0;
        }

        .info-item:last-child {
          border-bottom: none;
        }

        .info-label {
          color: #666;
        }

        .info-value {
          color: #333;
          font-weight: 500;
        }

        .help-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .help-item {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 10px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .help-item:hover {
          background: #e9ecef;
        }

        .help-icon {
          font-size: 1.5rem;
          width: 40px;
          text-align: center;
        }

        .help-info {
          flex: 1;
        }

        .help-info h4 {
          margin: 0 0 5px 0;
          color: #333;
          font-size: 1rem;
        }

        .help-info p {
          margin: 0;
          color: #666;
          font-size: 0.9rem;
        }

        .help-arrow {
          color: #999;
          font-size: 1.2rem;
          font-weight: bold;
        }

        .danger-card {
          border: 2px solid #dc3545;
          background: #fff5f5;
        }

        .danger-card h3 {
          color: #dc3545;
        }

        .danger-content p {
          color: #666;
          margin-bottom: 15px;
          text-align: center;
        }

        .btn-danger {
          background: #dc3545;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .btn-danger:hover {
          background: #c82333;
        }

        .btn-full {
          width: 100%;
        }

        /* 푸시 알림 설정 스타일 */
        .notification-status {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .status-info p {
          margin: 8px 0;
          font-size: 0.9rem;
        }

        .demo-badge {
          color: #ff6b00;
          font-weight: bold;
          font-size: 0.85rem;
          background: #fff3e0;
          padding: 4px 8px;
          border-radius: 12px;
          display: inline-block;
          margin-top: 8px;
        }

        .notification-actions {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .notification-help {
          background: #f8f9fa;
          border-radius: 10px;
          padding: 15px;
          border-left: 4px solid #667eea;
        }

        .notification-help h4 {
          margin: 0 0 10px 0;
          color: #333;
          font-size: 1rem;
        }

        .notification-help ul {
          margin: 10px 0;
          padding-left: 20px;
        }

        .notification-help li {
          margin: 5px 0;
          color: #555;
          font-size: 0.9rem;
        }

        .help-note {
          margin: 12px 0 0 0;
          color: #666;
          font-size: 0.85rem;
          line-height: 1.4;
          padding-top: 10px;
          border-top: 1px solid #e0e0e0;
        }

        @media (max-width: 480px) {
          .profile-content {
            flex-direction: column;
            text-align: center;
            gap: 15px;
          }

          .setting-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .toggle-switch {
            align-self: flex-end;
          }
        }
      `}</style>
    </div>
  );
}

export default ChildSettings;